/**
 * Placeholder hygiene for the storefront (MERCH-55): landing-kit's section
 * defaults carry sample copy ("<p>Write something about your store.</p>",
 * "Your headline"…) that becomes real document content when an owner adds a
 * section and never edits it. Shoppers must not see it, so the storefront drops
 * what is empty or still that sample copy before landing-kit renders it.
 *
 * Interim, filtered on our side: the sample copy originates in landing-kit
 * (`sectionDefaults`) and TenantPage/TemplateRegion render it as content. An
 * upstream landing-kit ticket is needed for a placeholder/hint field (shown in
 * the editor, never rendered) instead of default content.
 *
 * PLACEHOLDER_FIELDS mirrors lib/store/storefrontQa.js (the pre-publish check);
 * tests/unit/storefrontQa.test.js keeps the two equal.
 */
import { sectionDefaults, SECTION_TYPES } from '@dreamtree-org/landing-kit/schema';

export const PLACEHOLDER_FIELDS = {
  hero_slider: ['slides[].heading'],
  promo_banner: ['heading'],
  testimonials: ['items[].quote', 'items[].name'],
  faq: ['items[].q', 'items[].a'],
  rich_text: ['html'],
  brand_strip: ['items[].name'],
};

/** Plain, normalised text of a string or rich-text html. */
const textOf = (v) => String(v == null ? '' : v).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

function valuesAt(props, field) {
  const [head, ...rest] = field.split('.');
  const list = head.endsWith('[]');
  const key = list ? head.slice(0, -2) : head;
  const v = props && props[key];
  if (!rest.length) return v === undefined ? [] : [v];
  if (list) return Array.isArray(v) ? v.flatMap((item) => valuesAt(item, rest.join('.'))) : [];
  return valuesAt(v, rest.join('.'));
}

let TEXTS;
/** Every sample text in the installed landing-kit's defaults (normalised). */
function placeholderTexts() {
  if (!TEXTS) {
    TEXTS = new Set();
    for (const [type, fields] of Object.entries(PLACEHOLDER_FIELDS)) {
      if (!SECTION_TYPES[type]) continue;
      const d = sectionDefaults(type);
      for (const f of fields) for (const v of valuesAt(d, f)) if (textOf(v)) TEXTS.add(textOf(v));
    }
  }
  return TEXTS;
}

/** True for text that is a landing-kit sample default. */
export const isPlaceholderText = (v) => placeholderTexts().has(textOf(v));

/** The text when it is real copy; '' when it is empty or a landing-kit sample default. */
export const shownText = (v) => (textOf(v) && !isPlaceholderText(v) ? String(v) : '');

/**
 * Sections or blocks without the ones that would only show sample copy: a text
 * block (rich_text) whose text is empty or still the default. Other sections
 * keep their own sample fields (the pre-publish check names them).
 */
export function withoutPlaceholderBlocks(blocks) {
  return (blocks || []).filter((b) => !(b && b.type === 'rich_text' && !shownText(b.props && b.props.html)));
}
